// Validation and normalization for Content Graph locale strings entered by the user.
//
// The canonical serialized form is the DASH form: a lowercase ISO-639 alpha-2 language,
// with an optional uppercase ISO-3166-1 alpha-2 country separated by a hyphen
// (e.g. "en", "en-US", "de-DE"). This mirrors the backend LocaleNormalizer so the
// frontend can give fast feedback before a value reaches the pipeline.

const SHAPE = /^[a-z]{2}(-[A-Z]{2})?$/;

/**
 * Normalizes a locale to the canonical dash form. Accepts either separator, lowercases the
 * language, uppercases the country, and converts "_" to "-". "NEUTRAL" is returned uppercased.
 * Returns the trimmed input for empty values.
 */
export function canonicalDash(locale: string): string {
  if (locale === null || locale === undefined) {
    return locale;
  }
  const trimmed = locale.trim();
  if (trimmed.length === 0) {
    return trimmed;
  }
  if (trimmed.toUpperCase() === 'NEUTRAL') {
    return 'NEUTRAL';
  }
  const parts = trimmed.split(/[-_]/);
  const base = parts[0].toLowerCase();
  if (parts.length >= 2 && parts[1].length > 0) {
    return `${base}-${parts[1].toUpperCase()}`;
  }
  return base;
}

// CLDR sentinel for an unknown region ("ZZ") / undetermined language ("und"). These resolve to a
// display name via Intl but are not real locales, so reject them explicitly.
const UNKNOWN_REGION = 'ZZ';

// Returns true only when the subtags actually exist per CLDR (via Intl.DisplayNames with
// fallback 'none', which yields undefined for unknown codes). This rejects structurally valid but
// non-existent tags like "zz" or "en-XX" that the SHAPE regex alone would accept. Note it cannot
// judge whether a real language/region pairing is sensible (e.g. "en-DE" is a real, if unusual,
// locale) — there is no supported-locale source from Graph to validate that against.
function hasRealSubtags(canonical: string): boolean {
  const [language, region] = canonical.split('-');
  try {
    const languageName = new Intl.DisplayNames(['en'], {type: 'language', fallback: 'none'}).of(language);
    if (!languageName) {
      return false;
    }
    if (region) {
      if (region === UNKNOWN_REGION) {
        return false;
      }
      const regionName = new Intl.DisplayNames(['en'], {type: 'region', fallback: 'none'}).of(region);
      if (!regionName) {
        return false;
      }
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns whether the given locale is a valid Content Graph locale (ISO-639 alpha-2 language with
 * optional ISO-3166-1 alpha-2 country), after normalization. "NEUTRAL" is accepted. The dash is the
 * only accepted subtag separator, so underscore forms like "en_US" are rejected. Beyond the
 * structural shape, the language and country subtags must be real codes known to Intl.DisplayNames,
 * so non-existent tags like "zz" or "en-XX" are rejected.
 */
export function isValidLocale(locale: string): boolean {
  if (!locale) {
    return false;
  }
  // Underscore is not an accepted separator; only the dash form (e.g. "en-US") is valid.
  if (locale.includes('_')) {
    return false;
  }
  const canonical = canonicalDash(locale);
  if (canonical === 'NEUTRAL') {
    return true;
  }
  if (!SHAPE.test(canonical)) {
    return false;
  }
  return hasRealSubtags(canonical);
}
